import { useRef, useState } from 'react'
import { httpsCallable } from 'firebase/functions'
import { logEvent } from 'firebase/analytics'
import { AnimatePresence, motion } from 'framer-motion'
import { Loader2, Play, Wifi, WifiOff, X } from 'lucide-react'
import { functions, analytics, isDevMode } from '@/lib/firebase'
import { useIsDesktop } from '@/hooks/useIsDesktop'

interface VideoData {
  url: string
  title: string
  durationSec: number
}

type State = 'idle' | 'loading' | 'open' | 'unavailable' | 'error'

function fmtDuration(sec: number) {
  if (!sec) return ''
  const m = Math.floor(sec / 60)
  const s = sec % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

function useNetworkState() {
  const conn = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection
  const effectiveType: string = conn?.effectiveType ?? '4g'
  const offline = !navigator.onLine
  const slow = effectiveType === 'slow-2g' || effectiveType === '2g'
  return { offline, slow, wifiOnly: offline || slow }
}

// ─── Video player shared between mobile sheet and desktop modal ─────────────

function VideoPlayer({ data, slug }: { data: VideoData; slug: string }) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [played, setPlayed] = useState(false)

  const handlePlay = () => {
    if (played) return
    setPlayed(true)
    if (analytics) logEvent(analytics, 'video_play', { slug, title: data.title })
  }

  return (
    <div className="w-full">
      <video
        ref={videoRef}
        src={data.url}
        controls
        playsInline
        preload="metadata"
        onPlay={handlePlay}
        className="w-full rounded-xl bg-black aspect-video"
      />
      {data.durationSec > 0 && (
        <p className="text-[11px] text-gray-400 mt-1 text-right tabular">
          {fmtDuration(data.durationSec)}
        </p>
      )}
    </div>
  )
}

// ─── Mobile bottom sheet ────────────────────────────────────────────────────

function MobileVideoSheet({ data, slug, onClose }: { data: VideoData; slug: string; onClose: () => void }) {
  return (
    <motion.div
      className="fixed inset-0 z-[80] flex items-end justify-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <motion.div
        className="relative z-10 w-full bg-white rounded-t-3xl shadow-2xl pb-[env(safe-area-inset-bottom,16px)]"
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', stiffness: 340, damping: 34 }}
      >
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-9 h-1 bg-gray-200 rounded-full" />
        </div>
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
          <p className="font-display font-bold text-[15px] text-gray-900 truncate pr-4">{data.title}</p>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition flex-shrink-0"
          >
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>
        <div className="p-4">
          <VideoPlayer data={data} slug={slug} />
        </div>
      </motion.div>
    </motion.div>
  )
}

// ─── Desktop centered dialog ─────────────────────────────────────────────────

function DesktopVideoModal({ data, slug, onClose }: { data: VideoData; slug: string; onClose: () => void }) {
  return (
    <motion.div
      className="fixed inset-0 z-[80] flex items-center justify-center p-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        className="relative z-10 w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden"
        initial={{ scale: 0.94, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.94, opacity: 0 }}
        transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <p className="font-display font-bold text-[15px] text-gray-900">{data.title}</p>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition"
          >
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>
        <div className="p-5">
          <VideoPlayer data={data} slug={slug} />
        </div>
      </motion.div>
    </motion.div>
  )
}

// ─── Public component ────────────────────────────────────────────────────────

export interface VideoExplainerProps {
  slug: string
  label?: string
}

const getVideoUrlFn = httpsCallable<{ slug: string }, VideoData>(functions, 'getVideoUrl')

export function VideoExplainer({ slug, label = 'Voir comment ça marche' }: VideoExplainerProps) {
  const [state, setState] = useState<State>('idle')
  const [videoData, setVideoData] = useState<VideoData | null>(null)
  const isDesktop = useIsDesktop()
  const { wifiOnly } = useNetworkState()

  const handleClick = async () => {
    if (state === 'loading') return

    if (isDevMode()) {
      setVideoData({
        url: 'https://storage.googleapis.com/gtv-videos-bucket/sample/ForBiggerEscapes.mp4',
        title: label,
        durationSec: 15,
      })
      setState('open')
      return
    }

    setState('loading')
    try {
      const res = await getVideoUrlFn({ slug })
      setVideoData(res.data)
      setState('open')
    } catch (err: unknown) {
      const code = (err as { code?: string }).code
      if (code === 'functions/not-found') {
        setState('unavailable')
      } else {
        setState('error')
      }
    }
  }

  const close = () => setState('idle')

  if (wifiOnly) {
    return (
      <button
        disabled
        className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-gray-400 cursor-not-allowed"
      >
        <WifiOff className="w-3.5 h-3.5" />
        Disponible en WiFi uniquement
      </button>
    )
  }

  return (
    <>
      <div className="flex flex-col items-center gap-1">
        <button
          onClick={handleClick}
          disabled={state === 'loading'}
          className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-green-700 hover:text-green-800 transition disabled:opacity-60"
        >
          {state === 'loading'
            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
            : <Play className="w-3.5 h-3.5 fill-current" />}
          {state === 'loading' ? 'Chargement…' : label}
        </button>

        {(state === 'unavailable' || state === 'error') && (
          <p className="text-[11px] text-gray-400">Vidéo temporairement indisponible</p>
        )}
      </div>

      <AnimatePresence>
        {state === 'open' && videoData && (
          isDesktop
            ? <DesktopVideoModal key="desktop" data={videoData} slug={slug} onClose={close} />
            : <MobileVideoSheet key="mobile" data={videoData} slug={slug} onClose={close} />
        )}
      </AnimatePresence>
    </>
  )
}
